import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';

import {
  Checklist,
  ChecklistCommitParams,
  ChecklistCondition,
  ChecklistConditionQueryParams,
  ChecklistConditionsCreateParams,
  ChecklistCreateParams,
  ChecklistItem,
  ChecklistItemQueryParams,
  ChecklistItemsCreateParams,
  ChecklistLink,
  ChecklistLinkQueryParams,
  ChecklistQueryParams,
  ChecklistSaveParams,
} from '../models/checklist.model';
import { CloudStorage } from '../models/cloud-storage.model';
import { ListResponse } from '../models/utility.model';
import { ChecklistGateway } from '../usecases/checklist.gateway';

@Injectable()
export class ChecklistService extends ChecklistGateway {
  constructor(private _http: HttpClient) {
    super();
  }

  listChecklists(params: ChecklistQueryParams): Observable<ListResponse<Checklist>> {
    return this._http.get<ListResponse<Checklist>>('api/vgs/checklists', { params });
  }

  createChecklist(params: ChecklistCreateParams): Observable<Checklist> {
    return this._http.post<Checklist>(`api/vgs/checklists`, params);
  }

  saveChecklist(params: ChecklistSaveParams): Observable<Checklist> {
    return this._http.post<Checklist>(`api/vgs/checklists/save`, params);
  }

  commitChecklist(params: ChecklistCommitParams): Observable<Checklist> {
    return this._http.post<Checklist>(`api/vgs/checklists/commit`, params);
  }

  deleteChecklist(checklistId: string): Observable<void> {
    return this._http.delete<void>(`api/vgs/checklists/${checklistId}`);
  }

  listChecklistLinks(checklistId: string, params: ChecklistLinkQueryParams): Observable<ListResponse<ChecklistLink>> {
    return this._http.get<ListResponse<ChecklistLink>>(`api/vgs/checklists/${checklistId}/links`, { params });
  }

  getChecklistFlowchart(checklistId: string): Observable<string> {
    return this._http
      .get<{ url: string }>(`api/vgs/checklists/${checklistId}/flowchart`)
      .pipe(switchMap(({ url }) => this._http.get(url, { responseType: 'text' })));
  }

  createChecklistFlowchart(checklistId: string): Observable<CloudStorage> {
    return this._http.post<CloudStorage>(`api/vgs/checklists/${checklistId}/flowchart`, {});
  }

  listChecklistItems(checklistId: string, params: ChecklistItemQueryParams): Observable<ListResponse<ChecklistItem>> {
    return this._http.get<ListResponse<ChecklistItem>>(`api/vgs/checklists/${checklistId}/items`, { params });
  }

  createChecklistItems(checklistId: string, params: ChecklistItemsCreateParams): Observable<ListResponse<ChecklistItem>> {
    return this._http.post<ListResponse<ChecklistItem>>(`api/vgs/checklists/${checklistId}/items`, params);
  }

  listChecklistConditions(checklistId: string, params: ChecklistConditionQueryParams): Observable<ListResponse<ChecklistCondition>> {
    return this._http.get<ListResponse<ChecklistCondition>>(`api/vgs/checklists/${checklistId}/conditions`, { params });
  }

  createChecklistConditions(checklistId: string, params: ChecklistConditionsCreateParams): Observable<ListResponse<ChecklistCondition>> {
    return this._http.post<ListResponse<ChecklistCondition>>(`api/vgs/checklists/${checklistId}/conditions`, params);
  }
}
